import { useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { BaseSuggestionData, SuggestionDataSource } from '@jackstenglein/mui-mentions';
import { EmployeeSearchResult } from '@dap/memberadmin/types';
import { useEmployeeSearchCache } from './useEmployeeSearchCache';
import { filterMentionCandidates, useFeedMentionScope } from './useFeedMentionScope';

export interface MentionSuggestionData extends BaseSuggestionData {
  jobTitle?: string;
}

const DEBOUNCE_MS = 300;

// Mirrors the double-bracket token format in @dap/social-feed/utils' mentionTokens.ts.
// Must NOT carry the "g" flag - mui-mentions throws if given a regex with flags.
const MENTION_MARKUP = '@[[__id__]]';
const MENTION_REGEX = /@\[\[([^\]]+)\]\]/;

const toSuggestions = (employees: EmployeeSearchResult[]): MentionSuggestionData[] =>
  employees.map((employee) => ({
    id: employee.userId,
    display: employee.name,
    jobTitle: employee.jobTitle,
  }));

/**
 * Builds a mui-mentions SuggestionDataSource backed by the brand-scoped employee
 * search API (for a common feed, every brand it's shown in - see useFeedMentionScope).
 * The returned object has no `displayTransform` - callers (AddComment, EditComment)
 * supply their own, since adding a new mention only needs the identity fallback while
 * editing an existing comment needs a pre-resolved userId->name map.
 * Longer queries are narrowed from earlier results where possible (see useEmployeeSearchCache).
 *
 * When `socialFeedId` points at a feed limited to certain users, the search API is
 * skipped and only those users are offered.
 */
export function useCommentMentionDataSource(
  socialFeedId?: string
): SuggestionDataSource<MentionSuggestionData> {
  const scope = useFeedMentionScope(socialFeedId);
  const { fetchEmployees, fromCache } = useEmployeeSearchCache();

  // mui-mentions invokes `data(query)` on every keystroke with no built-in
  // debounce (confirmed in its SuggestionsOverlay source) - debounce ourselves.
  const debouncedFetch = useMemo(
    () =>
      debounce(
        (query: string, brandKeys: string[], resolve: (value: MentionSuggestionData[]) => void) => {
          fetchEmployees(query, brandKeys)
            .then((results) => resolve(toSuggestions(results)))
            .catch(() => resolve([]));
        },
        DEBOUNCE_MS
      ),
    [fetchEmployees]
  );

  const data = useCallback(
    (query: string) =>
      new Promise<MentionSuggestionData[]>((resolve) => {
        const trimmed = query.trim();
        if (!trimmed || scope.status === 'pending') {
          resolve([]);
          return;
        }
        if (scope.status === 'restricted') {
          scope
            .loadAllowedUsers()
            .then((allowed) =>
              resolve(
                filterMentionCandidates(allowed, trimmed).map((employee) => ({
                  id: employee.userId,
                  display: employee.name,
                  jobTitle: employee.jobTitle ?? undefined,
                }))
              )
            )
            .catch(() => resolve([]));
          return;
        }
        const { brandKeys } = scope;
        const cached = fromCache(trimmed, brandKeys);
        if (!cached) {
          debouncedFetch(trimmed, brandKeys, resolve);
          return;
        }
        debouncedFetch.cancel();
        cached.then((results) => {
          if (results) {
            resolve(toSuggestions(results));
          } else {
            debouncedFetch(trimmed, brandKeys, resolve);
          }
        });
      }),
    [debouncedFetch, fromCache, scope]
  );

  // Must be referentially stable: mui-mentions clears and re-queries its suggestions
  // whenever `dataSources` changes, which makes the popup blink on every re-render.
  return useMemo(
    () => ({
      trigger: '@',
      markup: MENTION_MARKUP,
      regex: MENTION_REGEX,
      data,
      showAvatar: true,
      avatarSx: { width: 28, height: 28, fontSize: 14 },
      appendSpaceOnAdd: true,
    }),
    [data]
  );
}
